import { CopyOutlined } from '@ant-design/icons';
import * as Sentry from '@sentry/react';
import Loader from '@zeus/core/Loader';
import NotExists from '_/components/common/NotExists';
import { Alert, Button, Typography, Space } from 'antd';
import React, { Component, PropsWithChildren, ReactElement } from 'react';

interface IAsyncPageContainerProps {
  isLoading: boolean;
  notFound?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  eventId: string | null;
}

class AsyncPageContainerErrorBoundary extends Component<PropsWithChildren<IAsyncPageContainerProps>, ErrorBoundaryState> {
  constructor(props: PropsWithChildren<IAsyncPageContainerProps>) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error: error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Report to Sentry
    Sentry.withScope((scope) => {
      scope.setExtras(errorInfo as Record<string, unknown>);
      const eventId = Sentry.captureException(error);
      this.setState({ errorInfo: errorInfo, eventId: eventId });
    });

    // Log to console for development
    console.error('Error caught by AsyncPageContainer:', error, errorInfo);
  }

  handleCopyError = () => {
    const { error, errorInfo } = this.state;
    const errorText = `Error: ${error?.message || 'Unknown error'}

Stack Trace:
${error?.stack || 'No stack trace available'}

Component Stack:
${errorInfo?.componentStack || 'No component stack available'}`;

    navigator.clipboard.writeText(errorText).then(() => {
      // Could show a success message here
      alert('Error details copied to clipboard!');
    });
  };

  handleRefresh = () => {
    window.location.reload();
  };

  render(): React.ReactNode {
    const { hasError, error, errorInfo } = this.state;
    const { isLoading, notFound = false, children } = this.props;

    if (hasError) {
      return (
        <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Alert
              message="An Error Occurred"
              description="Something went wrong while rendering this page. Please copy the error details below and send them to the development team."
              type="error"
              showIcon
            />

            <div>
              <Typography.Title level={4}>Error Message</Typography.Title>
              <Alert title={error?.message || 'Unknown error'} type="error" style={{ marginBottom: 16 }} />

              <Typography.Title level={4}>Stack Trace</Typography.Title>
              <pre
                style={{
                  background: '#f5f5f5',
                  padding: '16px',
                  borderRadius: '4px',
                  overflow: 'auto',
                  maxHeight: '300px',
                  fontSize: '12px',
                  border: '1px solid #d9d9d9',
                }}
              >
                {error?.stack || 'No stack trace available'}
              </pre>

              <Typography.Title level={4} style={{ marginTop: 16 }}>
                Component Stack
              </Typography.Title>
              <pre
                style={{
                  background: '#f5f5f5',
                  padding: '16px',
                  borderRadius: '4px',
                  overflow: 'auto',
                  maxHeight: '200px',
                  fontSize: '12px',
                  border: '1px solid #d9d9d9',
                }}
              >
                {errorInfo?.componentStack || 'No component stack available'}
              </pre>
            </div>

            <Space>
              <Button type="primary" icon={<CopyOutlined />} onClick={this.handleCopyError}>
                Copy Error Details
              </Button>
              <Button onClick={this.handleRefresh}>Refresh Page</Button>
            </Space>
          </Space>
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="wb-flexbox wb-flexColumn wb-fullWidth wb-fullHeight wb-alignItems_center">
          <Loader />
        </div>
      );
    }

    if (notFound) {
      return <NotExists />;
    }

    return <>{children}</>;
  }
}

// Export as default for backward compatibility
const AsyncPageContainer = (props: PropsWithChildren<IAsyncPageContainerProps>): ReactElement => (
  <AsyncPageContainerErrorBoundary {...props} />
);

export default AsyncPageContainer;
